import { useState } from 'react';
import { Link } from 'react-router-dom';
import { solidsData } from './data/solidsData';
import SolidVisual from './components/SolidVisual';
import NetDiagram from './components/NetDiagram';
import InteractiveCalculator from './components/InteractiveCalculator';
import CrossSectionExplorer from './components/CrossSectionExplorer';
import PracticeQuiz from './components/PracticeQuiz';

export default function ThreeDModelingPage() {
  const [selectedSolid, setSelectedSolid] = useState(solidsData[0]);
  const [activeTab, setActiveTab] = useState<'visual' | 'net' | 'calculator' | 'cross-section' | 'practice'>('visual');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <Link
            to="/grades/grade-10"
            className="inline-flex items-center gap-2 text-slate-300 hover:text-white mb-6 text-sm font-medium transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Grade 10
          </Link>
          <h1 className="text-4xl font-bold mb-4">Interactive 3D Modeling Activities</h1>
          <p className="text-lg text-slate-300 max-w-3xl">
            Explore prisms, pyramids, cylinders, cones, and spheres with interactive 3D visuals, nets, calculators, and cross-section explorers. Master volume and surface area formulas!
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Solid Selector */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Select a 3D Solid</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
            {solidsData.map((solid) => {
              const isSelected = selectedSolid.id === solid.id;
              let colorClass = '';
              
              switch (solid.category) {
                case 'prism':
                  colorClass = isSelected ? 'bg-teal-600 text-white' : 'bg-teal-50 text-teal-700 hover:bg-teal-100';
                  break;
                case 'pyramid':
                  colorClass = isSelected ? 'bg-violet-600 text-white' : 'bg-violet-50 text-violet-700 hover:bg-violet-100';
                  break;
                case 'cylinder':
                  colorClass = isSelected ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100';
                  break;
                case 'cone':
                  colorClass = isSelected ? 'bg-orange-600 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100';
                  break;
                case 'sphere':
                  colorClass = isSelected ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100';
                  break;
              }

              return (
                <button
                  key={solid.id}
                  onClick={() => {
                    setSelectedSolid(solid);
                    setActiveTab('visual');
                  }}
                  className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${colorClass} ${
                    isSelected ? 'border-transparent shadow-lg scale-105' : 'border-slate-200'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">
                      {solid.category === 'prism' && '📦'}
                      {solid.category === 'pyramid' && '🔺'}
                      {solid.category === 'cylinder' && '🥫'}
                      {solid.category === 'cone' && '🍦'}
                      {solid.category === 'sphere' && '⚽'}
                    </div>
                    <p className="text-xs font-bold whitespace-nowrap">{solid.name}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Solid Info */}
        <div className="bg-white rounded-xl p-6 border-2 border-slate-200 mb-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-3">{selectedSolid.name}</h2>
          <p className="text-sm text-slate-700 mb-4">{selectedSolid.description}</p>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-indigo-50 rounded-lg p-4 border-2 border-indigo-200">
              <p className="text-xs font-semibold text-indigo-900 mb-2">Volume Formula</p>
              <p className="text-lg font-bold text-indigo-700">{selectedSolid.volumeFormula}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
              <p className="text-xs font-semibold text-purple-900 mb-2">Surface Area Formula</p>
              <p className="text-lg font-bold text-purple-700">{selectedSolid.surfaceAreaFormula}</p>
            </div>
          </div>

          <div className="bg-amber-50 rounded-lg p-4 border-2 border-amber-200">
            <p className="text-xs font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <i className="ri-lightbulb-line"></i>
              Real-World Application
            </p>
            <p className="text-sm text-amber-900">{selectedSolid.realWorldApplication}</p>
          </div>
        </div>

        {/* Worked Example */}
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border-2 border-teal-200 mb-8">
          <h3 className="text-xl font-bold text-teal-900 mb-4 flex items-center gap-2">
            <i className="ri-file-list-3-line"></i>
            Worked Example
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-teal-800 mb-1">Given Dimensions:</p>
              <p className="text-sm text-slate-700">{selectedSolid.workedExample.dimensions}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-teal-800 mb-1">Volume Calculation:</p>
              <p className="text-sm text-slate-700">{selectedSolid.workedExample.volumeCalculation}</p>
              <p className="text-base font-bold text-teal-700 mt-1">Answer: {selectedSolid.workedExample.volumeAnswer}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-teal-800 mb-1">Surface Area Calculation:</p>
              <p className="text-sm text-slate-700">{selectedSolid.workedExample.surfaceAreaCalculation}</p>
              <p className="text-base font-bold text-teal-700 mt-1">Answer: {selectedSolid.workedExample.surfaceAreaAnswer}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveTab('visual')}
              className={`px-6 py-3 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'visual'
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <i className="ri-3d-box-line mr-2"></i>
              3D Visual
            </button>
            <button
              onClick={() => setActiveTab('net')}
              className={`px-6 py-3 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'net'
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <i className="ri-layout-grid-line mr-2"></i>
              Net Diagram
            </button>
            <button
              onClick={() => setActiveTab('calculator')}
              className={`px-6 py-3 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'calculator'
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <i className="ri-calculator-line mr-2"></i>
              Calculator
            </button>
            <button
              onClick={() => setActiveTab('cross-section')}
              className={`px-6 py-3 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'cross-section'
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <i className="ri-scissors-cut-line mr-2"></i>
              Cross-Sections
            </button>
            <button
              onClick={() => setActiveTab('practice')}
              className={`px-6 py-3 rounded-lg font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'practice'
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <i className="ri-question-answer-line mr-2"></i>
              Practice ({selectedSolid.questions.length} Questions)
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'visual' && (
            <SolidVisual solidId={selectedSolid.id} name={selectedSolid.name} />
          )}
          {activeTab === 'net' && (
            <NetDiagram
              solidId={selectedSolid.id}
              name={selectedSolid.name}
              description={selectedSolid.netDescription}
            />
          )}
          {activeTab === 'calculator' && (
            <InteractiveCalculator
              solidId={selectedSolid.id}
              name={selectedSolid.name}
              volumeFormula={selectedSolid.volumeFormula}
              surfaceAreaFormula={selectedSolid.surfaceAreaFormula}
            />
          )}
          {activeTab === 'cross-section' && (
            <CrossSectionExplorer
              solidId={selectedSolid.id}
              name={selectedSolid.name}
              crossSections={selectedSolid.crossSections}
            />
          )}
          {activeTab === 'practice' && (
            <PracticeQuiz questions={selectedSolid.questions} solidName={selectedSolid.name} />
          )}
        </div>

        {/* Teacher Tips */}
        <div className="mt-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-200">
          <h3 className="text-lg font-bold text-indigo-900 mb-3 flex items-center gap-2">
            <i className="ri-user-star-line"></i>
            Mr. Augustine's Teaching Tips
          </h3>
          <div className="space-y-2 text-sm text-slate-700">
            <p>
              <strong>• Visualization is Key:</strong> Encourage students to rotate the 3D models and sketch them from different angles to build spatial reasoning skills.
            </p>
            <p>
              <strong>• Connect to Real Life:</strong> Ask students to find examples of each solid in their daily lives (soup cans, ice cream cones, basketballs, etc.).
            </p>
            <p>
              <strong>• Formula Mastery:</strong> Have students derive formulas by relating 3D solids to simpler shapes (e.g., a cone is 1/3 of a cylinder with the same base and height).
            </p>
            <p>
              <strong>• Cross-Section Practice:</strong> Use the cross-section explorer to prepare for standardized test questions about slicing 3D solids.
            </p>
            <p>
              <strong>• Calculator Practice:</strong> Students should practice both with and without calculators to build number sense and estimation skills.
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex flex-wrap gap-4 justify-center">
          <Link
            to="/grades/grade-10"
            className="px-6 py-3 bg-slate-700 text-white rounded-lg font-bold text-sm hover:bg-slate-800 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Grade 10 Hub
          </Link>
          <Link
            to="/grades/grade-10/constructions"
            className="px-6 py-3 bg-teal-600 text-white rounded-lg font-bold text-sm hover:bg-teal-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-compass-3-line mr-2"></i>
            Geometric Constructions
          </Link>
          <Link
            to="/grades/geometry-regents"
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-file-list-3-line mr-2"></i>
            Geometry Regents Prep
          </Link>
        </div>
      </div>
    </div>
  );
}